import { useEffect, useRef, useState } from "react";
import { Icon, Logo, type IconName } from "../../components/ui";
import { Link, useDocumentTitle } from "../../router";
import { homeFor, useSession } from "../../session";
import { employeeRoles } from "../../../shared/employees";
import { TOPUP_RULE_TEXT } from "../../../shared/ai-copy";
import { PRICING_FOOTNOTE } from "../../pricingCopy";
import { creditLine, formatRupees, PLATFORM_PRICE_NOTE, plans, PRICE_PER_MONTH } from "../../../shared/plans";
import { prefersReducedMotion, useReveal } from "./motion";
import { TeamBoard } from "./TeamBoard";
import "./landing.css";
import "./team.css";

const businesses = [
  "Kirana stores",
  "Boutiques",
  "Salons",
  "Dental clinics",
  "Coaching classes",
  "Caterers",
  "Interior studios",
  "AC repair",
  "Photographers",
  "Tour operators",
  "Bakeries",
  "PG & rentals",
  "Gyms",
  "Event decorators",
];

const languages = [
  {
    label: "English",
    text: "Thanks for asking! Could you tell me the size of your flat?",
  },
  { label: "Hinglish", text: "Namaste! Aapke flat ka size bata denge?" },
  {
    label: "हिन्दी",
    text: "नमस्ते! क्या आप अपने फ़्लैट का साइज़ बता सकते हैं?",
  },
  { label: "मराठी", text: "नमस्कार! तुमच्या फ्लॅटचा आकार सांगाल का?" },
  { label: "ગુજરાતી", text: "નમસ્તે! તમારા ફ્લેટની સાઇઝ જણાવશો?" },
  { label: "বাংলা", text: "নমস্কার! আপনার ফ্ল্যাটের মাপটা জানাবেন?" },
  { label: "தமிழ்", text: "வணக்கம்! உங்கள் வீட்டின் அளவைச் சொல்ல முடியுமா?" },
  { label: "తెలుగు", text: "నమస్తే! మీ ఫ్లాట్ సైజ్ చెప్పగలరా?" },
  { label: "ಕನ್ನಡ", text: "ನಮಸ್ಕಾರ! ನಿಮ್ಮ ಫ್ಲಾಟ್ ಗಾತ್ರ ತಿಳಿಸುತ್ತೀರಾ?" },
];

const steps: { kicker: string; title: string; body: string; icon: IconName }[] =
  [
    {
      kicker: "01 · Enquiry",
      title: "An enquiry lands at 11:42 pm.",
      body: "Customers write on WhatsApp, Instagram or your own Thekedar enquiry link. Your Sales Executive logs every one as a lead — their words, their number, and whether they agreed to hear back.",
      icon: "message",
    },
    {
      kicker: "02 · Draft",
      title: "They write back in your customer's language.",
      body: "They use only what you taught them — your prices, hours, area and answers. If they don't know something, they say you'll confirm. They never make up a price.",
      icon: "sparkles",
    },
    {
      kicker: "03 · Approve",
      title: "You approve with one tap.",
      body: "Or let routine replies go on their own. Payment links, discounts and anything unusual always wait for you. Pause the whole team with one switch.",
      icon: "approval",
    },
    {
      kicker: "04 · Paid",
      title: "Paid. Verified. Closed.",
      body: "Send a Razorpay link from the chat. The sale is marked won only when Razorpay confirms the money arrived — and your Follow-up Executive stops chasing. Your Office Manager puts it in tomorrow's report.",
      icon: "rupee",
    },
  ];

const modes = [
  {
    id: "copilot",
    label: "Copilot",
    line: "Your team drafts. You send.",
    lanes: { replies: false, followups: false },
  },
  {
    id: "supervised",
    label: "Supervised",
    line: "First replies go on their own. The rest waits for you.",
    lanes: { replies: true, followups: false },
  },
  {
    id: "autopilot",
    label: "Autopilot",
    line: "Replies and follow-ups go within your limits.",
    lanes: { replies: true, followups: true },
  },
] as const;

const trust: { icon: IconName; title: string; body: string; wide?: boolean }[] =
  [
    {
      icon: "plug",
      title: "“Connected” means verified",
      body: "WhatsApp and Razorpay show as connected only after we've checked your credentials with them. Nothing is ever pretended.",
      wide: true,
    },
    {
      icon: "lock",
      title: "Keys sealed with AES-256",
      body: "Provider tokens are encrypted before they touch the database and never reach your browser.",
    },
    {
      icon: "activity",
      title: "Every action has a receipt",
      body: "Who proposed it, who approved it, what the provider said. Kept for you.",
    },
    {
      icon: "pause",
      title: "One switch to stop",
      body: "Pause all AI actions instantly. Anyone on your team can pull the brake.",
    },
    {
      icon: "download",
      title: "Your data, always yours",
      body: "Export everything in one click or delete your workspace for good — never behind a paywall.",
    },
  ];

function Marquee() {
  const row = [...businesses, ...businesses];
  return (
    <div
      className="marquee"
      aria-label="Built for businesses like kirana stores, boutiques, salons, clinics, coaching classes, caterers and interior studios"
    >
      <div className="marquee__track" aria-hidden="true">
        {row.map((name, index) => (
          <span key={`${name}-${index}`} className="marquee__item">
            <i className="marquee__dot" /> {name}
          </span>
        ))}
      </div>
    </div>
  );
}

function Roster({ signedUpHref }: { signedUpHref: string }) {
  const hireable = employeeRoles.filter((role) => role.available);
  const front = hireable.filter(
    (role) =>
      role.id === "sales" ||
      role.id === "follow_up" ||
      role.id === "operations",
  );
  const backOffice = hireable.filter((role) => !front.includes(role));
  const soon = employeeRoles.filter((role) => !role.available);
  return (
    <section className="roster" id="team" aria-labelledby="team-title">
      <div className="section-head" data-reveal>
        <p className="kicker">Meet your team</p>
        <h2 id="team-title">
          Pick the ones you need. <em>Zero</em> days off.
        </h2>
        <p className="lead">
          Sales, follow-ups, bookings, invoices, complaints and marketing — each
          one has a job, follows your rules and keeps a record of everything
          they do. Add only the roles you need, and name each one yourself —
          Priya, Arjun, Kavya, anything. Five minutes. No salaries, no leave.
        </p>
      </div>
      <div className="roster__grid">
        {front.map((role, index) => (
          <article
            key={role.id}
            className="hire-card"
            data-reveal
            style={{
              ["--a" as string]: role.hue[0],
              ["--b" as string]: role.hue[1],
              ["--delay" as string]: `${index * 90}ms`,
            }}
          >
            <div className="hire-card__top">
              <span
                className="avatar avatar--lg"
                style={{
                  ["--a" as string]: role.hue[0],
                  ["--b" as string]: role.hue[1],
                }}
                aria-hidden="true"
              >
                {role.suggestedName[0]}
              </span>
              <div>
                <h3>{role.title}</h3>
                <p>You name them. We suggest “{role.suggestedName}”.</p>
              </div>
            </div>
            <p className="hire-card__pitch">{role.pitch}</p>
            <ul>
              {role.duties.map((duty) => (
                <li key={duty}>
                  <Icon name="check" size={15} /> {duty}
                </li>
              ))}
            </ul>
            <span className="duty">
              <i className="board__dot" /> Available · works 24×7
            </span>
          </article>
        ))}
      </div>
      {backOffice.length > 0 && (
        <div className="roster__back" data-reveal>
          <h3>And the back office</h3>
          <div className="roster__grid roster__grid--compact">
            {backOffice.map((role) => (
              <article
                key={role.id}
                className="hire-card hire-card--compact"
                style={{
                  ["--a" as string]: role.hue[0],
                  ["--b" as string]: role.hue[1],
                }}
              >
                <div className="hire-card__top">
                  <span
                    className="avatar"
                    style={{
                      ["--a" as string]: role.hue[0],
                      ["--b" as string]: role.hue[1],
                    }}
                    aria-hidden="true"
                  >
                    {role.suggestedName[0]}
                  </span>
                  <div>
                    <h3>{role.title}</h3>
                    <p>You name them. We suggest “{role.suggestedName}”.</p>
                  </div>
                </div>
                <p className="hire-card__pitch">{role.pitch}</p>
                <ul>
                  {role.duties.slice(0, 3).map((duty) => (
                    <li key={duty}>
                      <Icon name="check" size={15} /> {duty}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      )}
      <ol className="hiring" data-reveal>
        <li>
          <strong>Tell them about your business</strong>
          <span>
            Your prices, hours, area and common answers. Five minutes, or import
            it from your WhatsApp and Instagram profiles.
          </span>
        </li>
        <li>
          <strong>Connect your channels</strong>
          <span>
            Your enquiry link works instantly. WhatsApp, Instagram and Razorpay
            connect with a step-by-step guide.
          </span>
        </li>
        <li>
          <strong>They start their shift</strong>
          <span>
            Every enquiry, day and night, gets an answer. You approve what
            matters from your phone.
          </span>
        </li>
      </ol>
      {soon.length > 0 && (
        <div className="roster__soon" data-reveal>
          <h3>Joining the team soon</h3>
          <div className="soon-grid">
            {soon.map((role) => (
              <div key={role.id} className="soon-card">
                <span
                  className="avatar"
                  style={{
                    ["--a" as string]: role.hue[0],
                    ["--b" as string]: role.hue[1],
                  }}
                  aria-hidden="true"
                >
                  {role.title[0]}
                </span>
                <div>
                  <strong>
                    {role.title}
                  </strong>
                  <small>{role.pitch}</small>
                  <span className="soon-pill">Joining soon</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <p style={{ marginTop: 34 }} data-reveal>
        <Link className="btn btn--primary btn--lg" href={signedUpHref}>
          Hire your whole team — free <Icon name="arrowRight" size={18} />
        </Link>
      </p>
    </section>
  );
}

function Story() {
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLElement | null)[]>([]);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            setActive(Number((entry.target as HTMLElement).dataset.index));
        }
      },
      { rootMargin: "-45% 0px -45% 0px" },
    );
    refs.current.forEach((element) => element && observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="story" id="how" aria-labelledby="story-title">
      <div className="section-head" data-reveal>
        <p className="kicker">Watch them work</p>
        <h2 id="story-title">
          From “rate kya hai?” to <em>paid</em> — while you sleep.
        </h2>
      </div>
      <div className="story__grid">
        <div className="story__visual" aria-hidden="true">
          <div
            className="story__orbit"
            style={{ ["--active" as string]: active }}
          />
          <div className="story__cards" data-active={active}>
            <article className="scard scard--0">
              <header>
                <span className="scard__chip scard__chip--wa">WhatsApp</span>
                <span className="scard__time">11:42 pm</span>
              </header>
              <strong>Kavita Shah</strong>
              <p>
                “Namaste bhaiya, 2BHK ke liye modular kitchen ka rate kya hoga?”
              </p>
              <footer>
                <span className="scard__tag">New enquiry</span>
                <span className="scard__tag scard__tag--ok">Opted in</span>
              </footer>
            </article>
            <article className="scard scard--1">
              <header>
                <span className="scard__chip">
                  <Icon name="sparkles" size={13} /> Draft · Hinglish
                </span>
              </header>
              <p>
                “Namaste Kavita ji! Modular kitchen ₹1,50,000 se shuru hota hai.
                Aapka kitchen kitne feet ka hai?”
              </p>
              <footer>
                <span className="scard__source">
                  Price from: your price list
                </span>
              </footer>
            </article>
            <article className="scard scard--2">
              <header>
                <span className="scard__chip scard__chip--warn">
                  <Icon name="lock" size={13} /> Waiting for you
                </span>
              </header>
              <strong>Reply to Kavita on WhatsApp</strong>
              <div className="scard__buttons">
                <span>Reject</span>
                <span className="is-primary">
                  <Icon name="check" size={14} /> Approve
                </span>
              </div>
            </article>
            <article className="scard scard--3">
              <header>
                <span className="scard__chip scard__chip--ok">
                  <Icon name="check" size={13} /> Verified by Razorpay
                </span>
              </header>
              <strong className="scard__amount">₹25,000</strong>
              <p>Advance paid · lead moved to Won</p>
            </article>
          </div>
          <span className="story__example">Example</span>
        </div>
        <ol className="story__steps">
          {steps.map((step, index) => (
            <li
              key={step.kicker}
              className={`step ${active === index ? "is-active" : ""}`}
              data-index={index}
              ref={(element) => {
                refs.current[index] = element;
              }}
            >
              <span className="step__icon">
                <Icon name={step.icon} size={22} />
              </span>
              <p className="step__kicker">{step.kicker}</p>
              <h3>{step.title}</h3>
              <p className="step__body">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Autonomy() {
  const [mode, setMode] = useState<(typeof modes)[number]["id"]>("copilot");
  const current = modes.find((entry) => entry.id === mode)!;
  const lanes: { label: string; on: boolean; locked?: boolean }[] = [
    { label: "First replies", on: current.lanes.replies },
    { label: "Follow-ups", on: current.lanes.followups },
    { label: "Payment links", on: false, locked: true },
    { label: "Discounts & refunds", on: false, locked: true },
  ];
  return (
    <section className="autonomy" aria-labelledby="autonomy-title">
      <div className="autonomy__copy" data-reveal>
        <p className="kicker">You're the boss</p>
        <h2 id="autonomy-title">
          They work 24×7. <em>You</em> set the rules.
        </h2>
        <p className="lead">
          New employees start in Copilot — nothing goes out without you. Give
          them more freedom as they earn your trust. Money always waits for a
          human.
        </p>
      </div>
      <div className="dial" data-reveal>
        <div
          className="dial__switch"
          role="radiogroup"
          aria-label="Autonomy mode"
        >
          {modes.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="radio"
              aria-checked={mode === entry.id}
              className={mode === entry.id ? "is-on" : ""}
              onClick={() => setMode(entry.id)}
            >
              {entry.label}
            </button>
          ))}
          <span
            className="dial__thumb"
            style={{
              ["--index" as string]: modes.findIndex(
                (entry) => entry.id === mode,
              ),
            }}
            aria-hidden="true"
          />
        </div>
        <p className="dial__line" aria-live="polite">
          {current.line}
        </p>
        <ul className="lanes">
          {lanes.map((lane) => (
            <li
              key={lane.label}
              className={`lane ${lane.on ? "is-auto" : ""} ${lane.locked ? "is-locked" : ""}`}
            >
              <span className="lane__label">{lane.label}</span>
              <span className="lane__track" aria-hidden="true">
                <span className="lane__pulse" />
              </span>
              <span className="lane__state">
                <Icon name={lane.on ? "zap" : "lock"} size={14} />
                {lane.on
                  ? "Goes on its own"
                  : lane.locked
                    ? "Always waits for you"
                    : "Waits for you"}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Languages() {
  const [index, setIndex] = useState(1);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (touched || prefersReducedMotion()) return;
    const timer = window.setInterval(
      () => setIndex((value) => (value + 1) % languages.length),
      2600,
    );
    return () => window.clearInterval(timer);
  }, [touched]);
  return (
    <section className="langs" id="languages" aria-labelledby="langs-title">
      <div className="section-head" data-reveal>
        <p className="kicker">Speaks like your customers</p>
        <h2 id="langs-title">
          Nine languages. <em>One</em> team.
        </h2>
      </div>
      <div className="langs__stage" data-reveal>
        <p
          className="langs__quote"
          key={index}
          lang={index === 0 || index === 1 ? "en" : undefined}
        >
          {languages[index].text}
        </p>
        <div
          className="langs__chips"
          role="tablist"
          aria-label="Reply language"
        >
          {languages.map((language, chipIndex) => (
            <button
              key={language.label}
              type="button"
              role="tab"
              aria-selected={chipIndex === index}
              className={chipIndex === index ? "is-on" : ""}
              onClick={() => {
                setTouched(true);
                setIndex(chipIndex);
              }}
            >
              {language.label}
            </button>
          ))}
        </div>
        <p className="langs__note">
          The same question, as your Sales Executive would ask it. You pick the language when
          you set up.
        </p>
      </div>
    </section>
  );
}

export default function Landing() {
  useDocumentTitle("");
  const root = useRef<HTMLDivElement>(null);
  useReveal(root);
  const { me } = useSession();
  const signedIn = Boolean(me?.user);

  return (
    <div className="landing" ref={root}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <div className="scroll-progress" aria-hidden="true" />
      <header className="lp-nav">
        <Link href="/" aria-label="Thekedar home">
          <Logo tone="light" />
        </Link>
        <nav aria-label="Sections" className="lp-nav__links">
          <a href="#team">Your team</a>
          <a href="#how">How it works</a>
          <a href="#trust">Trust</a>
          <Link href="/creators">For creators</Link>
          <Link href="/demo">Demo</Link>
          <Link href="/community">Community</Link>
        </nav>
        <div className="lp-nav__actions">
          {signedIn ? (
            <Link className="btn btn--primary btn--sm" href={homeFor(me)}>
              Open Thekedar <Icon name="arrowRight" size={16} />
            </Link>
          ) : (
            <>
              <Link className="lp-nav__signin" href="/signin">
                Sign in
              </Link>
              <Link className="btn btn--primary btn--sm" href="/signup">
                Start free
              </Link>
            </>
          )}
        </div>
      </header>

      <main id="main">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__sky" aria-hidden="true">
            <span className="blob blob--1" />
            <span className="blob blob--2" />
            <span className="blob blob--3" />
            <span className="hero__stars" />
            <span className="hero__grain" />
          </div>
          <p className="hero__deva" aria-hidden="true">
            ठेकेदार
          </p>
          <div className="hero__inner">
            <div className="hero__copy">
              <p className="eyebrow">
                <span className="eyebrow__dot" /> AI employees for India's small
                businesses
              </p>
              <h1 id="hero-title" className="hero__title">
                <span className="line">
                  <span className="word">Hire</span>{" "}
                  <span className="word">AI</span>{" "}
                  <span className="word">employees.</span>
                </span>
                <span className="line">
                  <span className="word">They</span>{" "}
                  <span className="word">work</span>{" "}
                  <span className="word">while</span>{" "}
                  <span className="word word--serif">you sleep.</span>
                </span>
              </h1>
              <p className="hero__lead">
                Thekedar gives your business a team that's on duty 24×7. They
                answer customers at 2 am in their language, chase every quote,
                collect payments and hand you a report with your morning chai.
                You stay the boss.
              </p>
              <div className="hero__ctas">
                <Link
                  className="btn btn--primary btn--lg"
                  href={signedIn ? homeFor(me) : "/signup"}
                >
                  {signedIn ? "Open Thekedar" : "Hire your team — free"}{" "}
                  <Icon name="arrowRight" size={18} />
                </Link>
                <a className="btn btn--glass btn--lg" href="#team">
                  Meet the team
                </a>
              </div>
              <ul className="hero__meta">
                <li>
                  <Icon name="check" size={16} /> Free to hire, no card
                </li>
                <li>
                  <Icon name="check" size={16} /> WhatsApp and Instagram
                </li>
                <li>
                  <Icon name="check" size={16} /> Export or delete anytime
                </li>
              </ul>
            </div>
            <TeamBoard />
          </div>
        </section>

        <Marquee />
        <Roster signedUpHref={signedIn ? homeFor(me) : "/signup"} />
        <Story />
        <Autonomy />
        <Languages />

        <section className="trust" id="trust" aria-labelledby="trust-title">
          <div className="section-head" data-reveal>
            <p className="kicker">Nothing faked. Ever.</p>
            <h2 id="trust-title">
              Built to be <em>trusted</em> with your customers.
            </h2>
          </div>
          <div className="bento">
            {trust.map((item, index) => (
              <article
                key={item.title}
                className={`bento__card ${item.wide ? "bento__card--wide" : ""}`}
                data-reveal
                style={{ ["--delay" as string]: `${index * 70}ms` }}
              >
                <span className="bento__icon">
                  <Icon name={item.icon} size={22} />
                </span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
                {index === 0 && (
                  <div className="verify-demo" aria-hidden="true">
                    <span className="verify-demo__name">
                      <Icon name="message" size={16} /> WhatsApp Business
                    </span>
                    <span className="verify-demo__state">
                      <span className="verify-demo__checking">
                        <span className="spinner" /> Checking with Meta…
                      </span>
                      <span className="verify-demo__ok">
                        <Icon name="check" size={14} /> Connected
                      </span>
                    </span>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>

        <section className="early" aria-labelledby="early-title">
          <div className="early__card" data-reveal>
            <p className="kicker">Their salary</p>
            <h2 id="early-title">Hire the whole team for ₹0.</h2>
            <p>
              Your whole team joins free and never expires. Pay only when
              they're bringing in more work than the Free limits cover — you're
              never charged without choosing a plan yourself.
            </p>
            <div className="early__plans">
              {plans.map((plan) => (
                <div key={plan.id} className="early__plan">
                  <span>{plan.name}</span>
                  <strong>
                    {plan.pricePaise === 0
                      ? "₹0"
                      : formatRupees(plan.pricePaise)}
                  </strong>
                  <small>
                    {plan.pricePaise === 0 ? "forever" : PRICE_PER_MONTH} ·{" "}
                    {plan.seats === 1 ? "1 seat" : `${plan.seats} seats`} ·{" "}
                    {creditLine(plan.aiCreditPaise)}, ideal for up to{" "}
                    {plan.idealCustomers.toLocaleString("en-IN")} customers a
                    month
                  </small>
                </div>
              ))}
            </div>
            <p className="early__fine">
              {PRICING_FOOTNOTE} {PLATFORM_PRICE_NOTE} Need more? Top-ups of
              ₹99, ₹249 or ₹499 add the same amount of usage credit.{" "}
              {TOPUP_RULE_TEXT}
            </p>
            <ul>
              <li>
                <Icon name="check" size={16} /> Your own enquiry link and QR
                code
              </li>
              <li>
                <Icon name="check" size={16} /> AI-drafted replies in 9
                languages
              </li>
              <li>
                <Icon name="check" size={16} /> WhatsApp, Instagram and
                Razorpay, when you're ready
              </li>
            </ul>
            <Link
              className="btn btn--primary btn--lg"
              href={signedIn ? homeFor(me) : "/signup"}
            >
              Start free <Icon name="arrowRight" size={18} />
            </Link>
          </div>
        </section>

        <section className="finale" aria-labelledby="finale-title">
          <p className="finale__deva" aria-hidden="true">
            ठेकेदार
          </p>
          <h2 id="finale-title">
            Your business never sleeps.
            <br />
            <em>Now you can.</em>
          </h2>
          <Link
            className="btn btn--primary btn--lg"
            href={signedIn ? homeFor(me) : "/signup"}
          >
            Hire your team — free <Icon name="arrowRight" size={18} />
          </Link>
        </section>
      </main>

      <footer className="lp-footer">
        <Logo tone="light" size={26} />
        <nav aria-label="Legal">
          <Link href="/privacy">Privacy</Link>
          <Link href="/terms">Terms</Link>
          <Link href="/refunds">Refunds</Link>
          <Link href="/creators">For creators</Link>
          <Link href="/community">Community</Link>
          <Link href="/contact">Contact</Link>
          <a href="mailto:support@thekedar.work">support@thekedar.work</a>
        </nav>
        <p>© {new Date().getFullYear()} Thekedar. Made in India.</p>
      </footer>
    </div>
  );
}
